import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from '@store'
import { ROUTES } from '@constants'

// Layouts
import MainLayout from '@/layouts/MainLayout'
import AuthLayout from '@/layouts/AuthLayout'
import DashboardLayout from '@/layouts/DashboardLayout'
import SmartLayout from '@/layouts/SmartLayout'

// Pages publiques
import HomePage from '@pages/HomePage'
import ContactPage from "@pages/ContactPage";
import LoginPage from '@pages/auth/LoginPage'
import RegisterPage from '@pages/auth/RegisterPage'
import AdminLoginPage from '@pages/auth/AdminLoginPage'
import ForgotPasswordPage from '@pages/auth/ForgotPasswordPage'
import PhoneVerificationPage from '@pages/auth/PhoneVerificationPage'

// Pages privées (authentification requise)

import LigdiPage from '@pages/dashboard/DashboardPage'
import MyDashboardPage from '@pages/dashboard/MyDashboardPage'
import AdminDashboardPage from '@pages/admin/AdminDashboardPage'
import AdminClientsPage from '@pages/admin/AdminClientsPage'
import AdminPublicationsPage from '@pages/admin/AdminPublicationsPage'
import AdminVideosPage from '@pages/admin/AdminVideosPage'
import AdminBannersPage from '@pages/admin/AdminBannersPage'
import AdminMessagesPage from '@pages/admin/AdminMessagesPage'
import AdminUsersPage from '@pages/admin/AdminUsersPage'
import AdminRolesPage from '@pages/admin/AdminRolesPage'
import AdminActivitiesPage from '@pages/admin/AdminActivitiesPage'
import AdminCategoriesPage from '@pages/admin/AdminCategoriesPage'
import AdminPricingPage from '@pages/admin/AdminPricingPage'
import AdminTarifPage from '@pages/admin/AdminTarifPage'
import AdminVerificationPage from '@pages/admin/AdminVerificationPage'
import AdminSettingsPage from '@pages/admin/AdminSettingsPage'
import AdminHelpPage from '@pages/admin/AdminHelpPage'
import AdminTermsPage from '@pages/admin/AdminTermsPage'
import AdminPrivacyPage from '@pages/admin/AdminPrivacyPage'
import AdminProfilePage from '@pages/admin/AdminProfilePage'
import AdminSystemControlPage from '@pages/admin/AdminSystemControlPage'
import SecurityReportsPage from '@pages/admin/security/SecurityReportsPage'
import PersonalSecurityPage from '@pages/admin/security/PersonalSecurityPage'
import SystemSecurityPage from '@pages/admin/security/SystemSecurityPage'
import ProfilePage from '@pages/profile/ProfilePage'
import SettingsPage from '@pages/profile/SettingsPage'
import ProductDetailPage from '@pages/ProductDetailPage'
import VideoDetailPage from '@pages/VideoDetailPage'
import BannerDetailPage from '@pages/BannerDetailPage'
import StaffBannerDetailPage from '@pages/StaffBannerDetailPage'
import MessagesPage from '@pages/messages/MessagesPage'
import NotificationsPage from '@pages/notifications/NotificationsPage'
import CreatePostPage from '@pages/CreatePostPage'
import EditPostPage from '@pages/EditPostPage'
import CreateVideoPage from '@pages/CreateVideoPage'
import EditVideoPage from '@pages/EditVideoPage'
import CreateBannerPage from '@pages/CreateBannerPage'
import EditBannerPage from '@pages/EditBannerPage'
import PaymentPage from '@pages/PaymentPage'
import PaymentArticlePage from '@pages/PaymentArticlePage'
import PaymentVideoPage from '@pages/PaymentVideoPage'
import PaymentBannerPage from '@pages/PaymentBannerPage'
import FreeTrialDurationPage from '@pages/FreeTrialDurationPage'
import PaymentSuccessPage from '@pages/PaymentSuccessPage'
import PaymentErrorPage from '@pages/PaymentErrorPage'
import HelpPage from '@pages/HelpPage'
import AboutPage from '@pages/AboutPage'
import GuidePage from '@pages/GuidePage'
import PrivacyPage from '@pages/PrivacyPage'
import TermsPage from '@pages/TermsPage'
import PricingPage from '@pages/PricingPage'

// Pages d'erreur
import NotFoundPage from '@pages/NotFoundPage'

// Composant pour les routes protégées
const PrivateRoute = ({ children }) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  return isAuthenticated ? children : <Navigate to={ROUTES.LOGIN} replace />
}

// Composant pour les routes publiques (redirige si déjà connecté)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, userType } = useAuthStore((state) => ({
    isAuthenticated: state.isAuthenticated,
    userType: state.userType,
  }))
  if (!isAuthenticated) return children
  return <Navigate to={userType === 'admin' ? '/admin/dashboard' : ROUTES.LIGDI} replace />
}

function AppRoutes() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Routes publiques — SmartLayout : DashboardLayout si connecté, MainLayout sinon */}
        <Route element={<SmartLayout />}>
          <Route path={ROUTES.HOME} element={<HomePage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/help" element={<HelpPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/guide" element={<GuidePage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
        </Route>

        {/* Routes d'authentification */}
        <Route element={<AuthLayout />}>
          <Route
            path={ROUTES.LOGIN}
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />
          <Route
            path={ROUTES.REGISTER}
            element={
              <PublicRoute>
                <RegisterPage />
              </PublicRoute>
            }
          />
          <Route
            path={ROUTES.FORGOT_PASSWORD}
            element={
              <PublicRoute>
                <ForgotPasswordPage />
              </PublicRoute>
            }
          />
          <Route
            path="/phone-verification"
            element={
              <PublicRoute>
                <PhoneVerificationPage />
              </PublicRoute>
            }
          />
        </Route>

        {/* Route de connexion admin (sans layout, sans PublicRoute) */}
        <Route path="/admin/login" element={<AdminLoginPage />} />

        {/* Routes admin (sans DashboardLayout) */}
        <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
        <Route path="/admin/clients" element={<AdminClientsPage />} />
        <Route path="/admin/publications" element={<AdminPublicationsPage />} />
        <Route path="/admin/videos" element={<AdminVideosPage />} />
        <Route path="/admin/banners" element={<AdminBannersPage />} />
        <Route path="/admin/messages" element={<AdminMessagesPage />} />
        <Route path="/admin/users" element={<AdminUsersPage />} />
        <Route path="/admin/roles" element={<AdminRolesPage />} />
        <Route path="/admin/activities" element={<AdminActivitiesPage />} />
        <Route path="/admin/categories" element={<AdminCategoriesPage />} />
        <Route path="/admin/pricing" element={<AdminPricingPage />} />
        <Route path="/admin/tarif" element={<AdminTarifPage />} />
        <Route path="/admin/verification" element={<AdminVerificationPage />} />
        <Route path="/admin/profile" element={<AdminProfilePage />} />
        <Route path="/admin/settings" element={<AdminSettingsPage />} />
        <Route path="/admin/help" element={<AdminHelpPage />} />
        <Route path="/admin/terms" element={<AdminTermsPage />} />
        <Route path="/admin/privacy" element={<AdminPrivacyPage />} />
        <Route path="/admin/security/reports" element={<SecurityReportsPage />} />
        <Route path="/admin/security/personal" element={<PersonalSecurityPage />} />
        <Route path="/admin/security/system" element={<SystemSecurityPage />} />
        <Route path="/admin/system-control" element={<AdminSystemControlPage />} />

        {/* Boutique + détails — publics ou connectés selon l'état d'auth */}
        <Route element={<SmartLayout />}>
          <Route path={ROUTES.LIGDI} element={<LigdiPage />} />
          <Route path="/product/:id" element={<ProductDetailPage />} />
          <Route path="/video/:id" element={<VideoDetailPage />} />
          <Route path="/banner/:id" element={<BannerDetailPage />} />
          <Route path="/staff-banner/:id" element={<StaffBannerDetailPage />} />
        </Route>

        {/* Routes privées */}
        <Route element={<DashboardLayout />}>
          <Route
            path={ROUTES.DASHBOARD}
            element={
              <PrivateRoute>
                <MyDashboardPage />
              </PrivateRoute>
            }
          />
          <Route
            path={ROUTES.PROFILE}
            element={
              <PrivateRoute>
                <ProfilePage />
              </PrivateRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <PrivateRoute>
                <SettingsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/messages"
            element={
              <PrivateRoute>
                <MessagesPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/notifications"
            element={
              <PrivateRoute>
                <NotificationsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/create-post"
            element={
              <PrivateRoute>
                <CreatePostPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/edit-post/:id"
            element={
              <PrivateRoute>
                <EditPostPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/payment"
            element={
              <PrivateRoute>
                <PaymentPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/payment-article"
            element={
              <PrivateRoute>
                <PaymentArticlePage />
              </PrivateRoute>
            }
          />
          <Route
            path="/free-trial-duration"
            element={
              <PrivateRoute>
                <FreeTrialDurationPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/payment-video"
            element={
              <PrivateRoute>
                <PaymentVideoPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/payment-banner"
            element={
              <PrivateRoute>
                <PaymentBannerPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/create-video"
            element={
              <PrivateRoute>
                <CreateVideoPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/edit-video/:id"
            element={
              <PrivateRoute>
                <EditVideoPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/create-banner"
            element={
              <PrivateRoute>
                <CreateBannerPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/edit-banner/:id"
            element={
              <PrivateRoute>
                <EditBannerPage />
              </PrivateRoute>
            }
          />
        </Route>

        {/* Pages de retour Wave */}
        <Route path="/payment-success" element={<PaymentSuccessPage />} />
        <Route path="/payment-error"   element={<PaymentErrorPage />} />

        {/* Route 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </BrowserRouter>
  )
}

export default AppRoutes
